/**
 * Alert Component
 * Notification and feedback messages
 */

import { AlertCircle, CheckCircle, Info, XCircle, X } from 'lucide-react';

export interface AlertProps {
    children: React.ReactNode;
    variant?: 'info' | 'success' | 'warning' | 'error';
    title?: string;
    dismissible?: boolean;
    onDismiss?: () => void;
    className?: string;
}

export function Alert({
    children,
    variant = 'info',
    title,
    dismissible = false,
    onDismiss,
    className = '',
}: AlertProps) {
    const variantConfig = {
        info: {
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            text: 'text-blue-800',
            icon: <Info className="w-5 h-5 text-blue-500" />,
        },
        success: {
            bg: 'bg-green-50',
            border: 'border-green-200',
            text: 'text-green-800',
            icon: <CheckCircle className="w-5 h-5 text-green-500" />,
        },
        warning: {
            bg: 'bg-amber-50',
            border: 'border-amber-200',
            text: 'text-amber-800',
            icon: <AlertCircle className="w-5 h-5 text-amber-500" />,
        },
        error: {
            bg: 'bg-red-50',
            border: 'border-red-200',
            text: 'text-red-800',
            icon: <XCircle className="w-5 h-5 text-red-500" />,
        },
    };

    const config = variantConfig[variant];

    return (
        <div
            className={`
                ${config.bg} ${config.border} ${config.text}
                border rounded-lg p-4
                ${className}
            `}
            role="alert"
        >
            <div className="flex">
                <div className="flex-shrink-0">{config.icon}</div>
                <div className="ml-3 flex-1">
                    {title && <h3 className="text-sm font-medium mb-1">{title}</h3>}
                    <div className="text-sm">{children}</div>
                </div>
                {dismissible && onDismiss && (
                    <button
                        onClick={onDismiss}
                        className="flex-shrink-0 ml-3 -mr-1 -mt-1 p-1 rounded-lg hover:bg-black/5 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}

export default Alert;
